// Карточка разбора документа. SMY-74, переселена в ленту в SMY-114
//
// Сценарий с наибольшей воспринимаемой ценностью: сквозной пример продукта
// построен на нём. Пропуски здесь такая же часть результата, как риски, —
// условие, которого в договоре НЕТ, человек сам не заметит, и блок
// «Пропущено» стоит рядом с рисками, а не в примечании.
//
// Вопросы второй стороне жили в правой колонке. Колонки не стало, и они
// переехали в свёрнутый блок под разбором: это то, что человек уносит с
// собой на встречу, а не то, на что он смотрит, пока читает риски.

import { useState } from 'react'
import { pluralize, type DocReview as DocReviewPayload, type RiskLevel, type UnitLabel } from '@smyslika/core'

import { Caps, Card, Divider, StatusDot, type ResultStatus } from '../ui/index.ts'
import { CardHead, CardNote, type CardProps } from './common.tsx'

/** Уровень риска — служебным цветом и словом. Значок без подписи не читается. */
const RISK: Record<RiskLevel, { status: ResultStatus; label: string }> = {
  high: { status: 'danger', label: 'высокий риск' },
  medium: { status: 'disputed', label: 'средний риск' },
  low: { status: 'none', label: 'низкий риск' },
}

const PAGE: UnitLabel = { one: 'страница', few: 'страницы', many: 'страниц' }
const QUESTION: UnitLabel = { one: 'вопрос', few: 'вопроса', many: 'вопросов' }

export function DocReviewCard({ artifact, runId }: CardProps) {
  const [note] = useState<string | null>(null)

  const card = artifact.payload as DocReviewPayload
  const file = card.file

  const meta = [
    file.pages != null ? `${file.pages} ${pluralize(file.pages, PAGE)}` : null,
    file.note,
    // Разобран не целиком — сказать прямо. Иначе пустой список пропусков
    // читается как «всё на месте», хотя дальше половины документа не дошли.
    file.truncated === true ? 'разобран не целиком' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Card className="rcard">
      {/* Имя файла стоит в сообщении человека, а не здесь: он его и прислал,
          и повторять это в ответе значит показывать одно и то же дважды. В
          шапке остаётся то, чего в вопросе не было — сколько страниц разобрано
          и в какой роли. */}
      <CardHead title="Разбор документа" meta={meta !== '' ? meta : undefined} {...(runId != null ? { runId } : {})} />

      <div>
        <Caps>Кратко</Caps>
        <p className="result__summary doc__summary">{card.summary}</p>
      </div>

      {card.risks.length > 0 ? (
        <>
          <Divider />
          <div>
            <Caps>Рискованные пункты · {card.risks.length}</Caps>
            <div className="doc__risks">
              {card.risks.map((r) => (
                <div key={r.title} className="result__claim">
                  <span className="result__claim-dot">
                    <StatusDot status={RISK[r.level].status} />
                  </span>
                  <span className="result__claim-body">
                    <span className="result__claim-text">{r.title}</span>
                    {/* Номер пункта — не украшение: по нему человек идёт в
                        документ, и выдумывать его нельзя. Нет — строка
                        остаётся с одним уровнем риска. */}
                    <span className="doc__risk-meta">
                      {[r.clause != null ? `пункт ${r.clause}` : null, RISK[r.level].label]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <Divider />
      <div>
        <Caps>Пропущено · {card.missing.length}</Caps>
        {/* Заголовок сам по себе не объясняет ничего: «пропущено» можно
            прочитать и как «я чего-то не сделал». Объясняем одной строкой —
            это условия, которых в документе НЕТ, и заметить их, читая сам
            документ, невозможно. */}
        <p className="doc__missing-why">Чего в документе нет, а стоило бы:</p>
        {card.missing.length === 0 ? (
          <p className="doc__missing-none">
            Существенных пропусков не нашлось — проверял {card.risks.length > 0 ? 'по тем же пунктам' : 'весь документ'}.
          </p>
        ) : (
          <ul className="doc__missing">
            {card.missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Вопросы открыты, а не спрятаны за раскрытием: это то, что человек
          понесёт второй стороне, — то есть ради чего разбор и заказан. */}
      {card.questions.length > 0 ? (
        <>
          <Divider />
          <div>
            <Caps>
              Вопросы второй стороне · {card.questions.length} {pluralize(card.questions.length, QUESTION)}
            </Caps>
            <ol className="doc__questions">
              {card.questions.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ol>
          </div>
        </>
      ) : null}

      {/* Кнопки «Скачать правки» здесь больше нет: правок в разборе не
          показано ни одной, и файл уносил бы то, чего человек не видел.
          Нужен документ — это отдельная задача «Создать документ», и там
          кнопка есть. */}
      <CardNote note={note} />

      {/* Граница ответственности лежит в артефакте, а не в вёрстке: она
          зависит от темы и от того, какие пункты нашлись. */}
      <p className="result__disclaimer">{card.disclaimer}</p>
    </Card>
  )
}
